import {
  priceRoomNight,
  type CreateItineraryComponentRequest,
  type HotelInventoryListItem,
  type HotelListItem,
  type HotelRateInput,
  type HotelRoomOut,
} from '@utazzo/shared';
import { UzBadge, UzButton, UzCheckbox, UzInput, UzSelect } from '@utazzo/ui';
import { BedDouble, ChevronDown, Loader2, MapPin, Search, Star, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { useItineraryDetail } from '../use-itineraries';

import { ComponentCard } from './ComponentCard';
import { payloadToDraft } from './itinerary-draft';
import { MealBadge } from './MealBadge';
import { AdminOnlyAmount } from './price-visibility';

import { useHotelInventory, useHotelRooms, useHotelsList } from '@/features/hotels/use-hotels';
import { formatDMY } from '@/lib/date-format';

/**
 * Preferred (in-house) hotel picker. The Live/TBO counterpart is
 * `TboPickerModal`; this one mirrors its shell + filters but sources hotels,
 * rooms and rate cards from our own Hotel master (`/hotels`, `/hotels/:id/rooms`,
 * `/hotels/:id/inventory`) instead of the TBO live search. Opened from the
 * Days step "Add hotel" placeholder when `itinerary.hotelSource === 'PREFERRED'`.
 *
 * Selection emits a `source: 'DB'` HOTEL component carrying `hotelId` +
 * room/meal/occupancy FKs and a stay-total `costPaise` (see the pricing note
 * on `stayTotalPaise`).
 */

/** "YYYY-MM-DD" + n days -> "YYYY-MM-DD" (UTC-consistent, no IST off-by-one). */
const addDay = (isoDate: string, n: number): string => {
  const d = new Date(isoDate + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

/** Map a free-form meal plan name/code to the meal-filter buckets, so the
 *  filter matches regardless of case or master naming ("Breakfast", "CP",
 *  "Bed & Breakfast" all -> bed_and_breakfast). Room-only / unknown -> null. */
const mealCategory = (s: string | null | undefined): string | null => {
  const t = (s ?? '').toLowerCase().trim();
  if (!t) return null;
  if (t.includes('full') || t === 'ap') return 'fullboard';
  if (t.includes('half') || t === 'map') return 'halfboard';
  if (t.includes('breakfast') || t === 'cp' || t === 'bb' || t === 'bp' || t === 'b&b')
    return 'bed_and_breakfast';
  return null;
};

interface PreferredHotelPickerModalProps {
  itineraryId: string;
  cityId: string | null;
  cityName: string | null;
  dayId: string | null;
  dayDate: string | null;
  /** Nights for this city leg; drives check-out + the stay-total price. */
  legNights: number | null;
  /** When set, picking a hotel REPLACES this draft component (swap-in-place). */
  replaceId?: string | null;
  pending: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateItineraryComponentRequest) => void | Promise<void>;
}

export const PreferredHotelPickerModal = ({
  itineraryId,
  cityId,
  cityName,
  dayDate,
  legNights,
  pending,
  onClose,
  onSubmit,
}: PreferredHotelPickerModalProps): React.JSX.Element => {
  const detail = useItineraryDetail(itineraryId).data ?? null;
  const currency = detail?.currencyCode ?? 'INR';

  const nights = Math.max(1, legNights ?? 1);
  const checkIn = dayDate ?? detail?.travelStartDate ?? '';
  const checkOut = checkIn ? addDay(checkIn, nights) : '';
  // The configured rooms + their occupancy - what the pricing engine prices.
  const configRooms = useMemo<
    { adults: number; children: number; childrenNoBed: number; infants: number }[]
  >(() => {
    const rooms = detail?.roomGroups?.flatMap((g) => g.rooms) ?? [];
    if (rooms.length === 0) return [{ adults: 2, children: 0, childrenNoBed: 0, infants: 0 }];
    return rooms.map((r) => ({
      adults: Math.max(1, r.adults),
      children: r.children,
      childrenNoBed: Math.min(r.childrenNoBed ?? 0, r.children),
      infants: r.infants,
    }));
  }, [detail?.roomGroups]);
  const roomsCount = configRooms.length;
  const roomsAreGlobal = (detail?.roomGroups?.flatMap((g) => g.rooms).length ?? 0) > 0;

  const [nameQ, setNameQ] = useState('');
  const [starsSel, setStarsSel] = useState<Set<number>>(new Set());
  const [mealSel, setMealSel] = useState<Set<string>>(new Set());
  const [thisCityOnly, setThisCityOnly] = useState(Boolean(cityId));
  const [sort, setSort] = useState<'name' | 'stars_desc' | 'stars_asc'>('stars_desc');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const q = nameQ.trim();
  const hotelsQuery = useHotelsList({
    page: 1,
    pageSize: 50,
    sortBy: 'name',
    sortDir: 'asc',
    status: 'ACTIVE',
    ...(thisCityOnly && cityId ? { cityId } : {}),
    ...(q ? { search: q } : {}),
  });

  const all: HotelListItem[] = useMemo(() => hotelsQuery.data?.data ?? [], [hotelsQuery.data]);

  const starOptions = useMemo(() => {
    const s = new Set<number>();
    for (const h of all) if (h.starRating > 0) s.add(h.starRating);
    return [...s].sort((a, b) => b - a);
  }, [all]);

  const filtered = useMemo(() => {
    const rows = all.filter((h) => {
      if (starsSel.size && !starsSel.has(h.starRating)) return false;
      return true;
    });
    return [...rows].sort((a, b) => {
      if (sort === 'name') return a.name.localeCompare(b.name);
      const diff = a.starRating - b.starRating;
      return sort === 'stars_asc' ? diff : -diff;
    });
  }, [all, starsSel, sort]);

  const toggleStar = (n: number): void => {
    const x = new Set(starsSel);
    if (x.has(n)) x.delete(n);
    else x.add(n);
    setStarsSel(x);
  };
  const toggleMeal = (code: string): void => {
    const x = new Set(mealSel);
    if (x.has(code)) x.delete(code);
    else x.add(code);
    setMealSel(x);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="flex h-[88vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-center justify-between border-b border-zinc-100 px-5 py-3.5">
          <div className="flex items-center gap-2">
            <BedDouble className="h-4 w-4 text-violet-600" aria-hidden />
            <h3 className="text-sm font-semibold tracking-tight text-zinc-900">Preferred hotels</h3>
            {cityName && (
              <span className="inline-flex items-center gap-1 text-xs text-zinc-500">
                <MapPin className="h-3 w-3" aria-hidden />
                {cityName}
              </span>
            )}
            {checkIn && checkOut && (
              <span className="text-xs text-zinc-400">
                {formatDMY(checkIn)} → {formatDMY(checkOut)} · {nights}N · {roomsCount} room
                {roomsCount === 1 ? '' : 's'}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex min-h-0 flex-1">
          {/* Left filter rail */}
          <aside className="hidden w-56 shrink-0 space-y-4 overflow-y-auto border-r border-zinc-100 px-4 py-4 md:block">
            {cityId && (
              <label className="flex items-center gap-2 text-xs text-zinc-700">
                <UzCheckbox
                  checked={thisCityOnly}
                  onCheckedChange={(v) => setThisCityOnly(Boolean(v))}
                />
                Only {cityName ?? 'this city'}
              </label>
            )}

            <div>
              <p className="mb-1.5 text-[11px] font-medium tracking-wide text-zinc-400 uppercase">
                Star rating
              </p>
              {starOptions.length === 0 ? (
                <p className="text-xs text-zinc-400">—</p>
              ) : (
                <div className="space-y-1">
                  {starOptions.map((n) => (
                    <label key={n} className="flex items-center gap-2 text-xs text-zinc-700">
                      <UzCheckbox checked={starsSel.has(n)} onCheckedChange={() => toggleStar(n)} />
                      <span className="inline-flex items-center gap-0.5">
                        {n}
                        <Star className="h-3 w-3 fill-amber-400 text-amber-400" aria-hidden />
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div>
              <p className="mb-1.5 text-[11px] font-medium tracking-wide text-zinc-400 uppercase">
                Meal plan
              </p>
              <div className="space-y-1">
                {[
                  { code: 'bed_and_breakfast', label: 'Breakfast' },
                  { code: 'halfboard', label: 'Half board (any two meals)' },
                  { code: 'fullboard', label: 'Full board (all three meals)' },
                ].map((m) => (
                  <label key={m.code} className="flex items-center gap-2 text-xs text-zinc-700">
                    <UzCheckbox
                      checked={mealSel.has(m.code)}
                      onCheckedChange={() => toggleMeal(m.code)}
                    />
                    {m.label}
                  </label>
                ))}
              </div>
              <p className="mt-1 text-[10px] leading-tight text-zinc-400">
                Applies to the room list inside each hotel.
              </p>
            </div>

            {(starsSel.size > 0 || mealSel.size > 0 || nameQ) && (
              <button
                type="button"
                onClick={() => {
                  setStarsSel(new Set());
                  setMealSel(new Set());
                  setNameQ('');
                }}
                className="text-xs font-medium text-violet-600 hover:text-violet-700"
              >
                Reset filters
              </button>
            )}
          </aside>

          {/* Right: search + results */}
          <section className="flex min-h-0 flex-1 flex-col">
            <div className="flex items-center gap-2 border-b border-zinc-100 px-4 py-3">
              <div className="relative flex-1">
                <Search
                  className="pointer-events-none absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2 text-zinc-400"
                  aria-hidden
                />
                <UzInput
                  name="hotelSearch"
                  className="pl-8"
                  placeholder="Search hotels by name..."
                  value={nameQ}
                  onChange={(e) => setNameQ(e.target.value)}
                />
              </div>
              <UzSelect
                name="sort"
                value={sort}
                onValueChange={(v) => setSort(v as typeof sort)}
                options={[
                  { value: 'stars_desc', label: 'Stars: high to low' },
                  { value: 'stars_asc', label: 'Stars: low to high' },
                  { value: 'name', label: 'Name (A–Z)' },
                ]}
              />
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
              {hotelsQuery.isLoading ? (
                <div className="flex items-center justify-center gap-2 py-10 text-sm text-zinc-400">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  Loading hotels...
                </div>
              ) : filtered.length === 0 ? (
                <div className="py-10 text-center text-sm text-zinc-400">
                  No preferred hotels found
                  {thisCityOnly && cityName ? ` in ${cityName}` : ''}. Try turning off the city
                  filter or add hotels in the Hotels master.
                </div>
              ) : (
                <ul className="space-y-2">
                  {filtered.map((h) => (
                    <HotelResultCard
                      key={h.id}
                      hotel={h}
                      itineraryId={itineraryId}
                      currency={currency}
                      nights={nights}
                      roomsCount={roomsCount}
                      roomsAreGlobal={roomsAreGlobal}
                      configRooms={configRooms}
                      checkIn={checkIn}
                      checkOut={checkOut}
                      cityName={cityName}
                      mealSel={mealSel}
                      expanded={expandedId === h.id}
                      pending={pending}
                      onToggle={() => setExpandedId(expandedId === h.id ? null : h.id)}
                      onPick={onSubmit}
                    />
                  ))}
                </ul>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

interface HotelResultCardProps {
  hotel: HotelListItem;
  itineraryId: string;
  currency: string;
  nights: number;
  roomsCount: number;
  /** True when configRooms are the itinerary's real rooms (not a fallback). */
  roomsAreGlobal: boolean;
  configRooms: { adults: number; children: number; childrenNoBed: number; infants: number }[];
  checkIn: string;
  checkOut: string;
  cityName: string | null;
  mealSel: Set<string>;
  expanded: boolean;
  pending: boolean;
  onToggle: () => void;
  onPick: (payload: CreateItineraryComponentRequest) => void | Promise<void>;
}

const HotelResultCard = ({
  hotel,
  itineraryId,
  currency,
  nights,
  roomsCount,
  roomsAreGlobal,
  configRooms,
  checkIn,
  checkOut,
  cityName,
  mealSel,
  expanded,
  pending,
  onToggle,
  onPick,
}: HotelResultCardProps): React.JSX.Element => {
  // Rates are admin-only; AdminOnlyAmount enforces that per-amount (sales sees
  // availability + capacity, never the price) - same as the live/TBO picker.
  // Rooms + rate cards fetched lazily, only when the card is expanded.
  const roomsQuery = useHotelRooms(expanded ? hotel.id : null);
  const invQuery = useHotelInventory(expanded ? hotel.id : null, {
    page: 1,
    pageSize: 100,
    sortDir: 'asc',
    ...(checkIn ? { onDate: checkIn } : {}),
  });

  const rooms = useMemo(() => (roomsQuery.data ?? []).filter((r) => r.isActive), [roomsQuery.data]);
  const invByRoom = useMemo(() => {
    const map = new Map<string, HotelInventoryListItem>();
    for (const inv of invQuery.data?.data ?? []) {
      if (inv.hotelRoomId && !map.has(inv.hotelRoomId)) map.set(inv.hotelRoomId, inv);
    }
    return map;
  }, [invQuery.data]);

  const visibleRooms = useMemo(() => {
    return rooms.filter((r) => {
      if (!mealSel.size) return true;
      // The meal filter buckets (bed_and_breakfast / halfboard / fullboard)
      // rarely match the free-form rate-card meal name/code verbatim, so map
      // each of the room's meal names/codes to a bucket and match any of them.
      const inv = invByRoom.get(r.id);
      const cats = new Set<string>();
      const add = (s: string | null | undefined): void => {
        const c = mealCategory(s);
        if (c) cats.add(c);
      };
      add(r.mealPlanCode);
      add(inv?.mealPlanName);
      for (const m of inv?.mealOptions ?? []) {
        add(m.mealPlanName);
        add(m.mealPlanCode);
      }
      return [...mealSel].some((sel) => cats.has(sel));
    });
  }, [rooms, mealSel, invByRoom]);

  // Price the FULL room config against this room type via the shared engine
  // (fixed-cost or per-pax, occupancy-aware). Returns the stay total in the
  // rate's currency + why it's unavailable (skip-and-flag), if so.
  const priceRoomType = (
    room: HotelRoomOut,
    inv: HotelInventoryListItem | null,
  ): { total: number; perRoom: number[]; unavailable: string | null } => {
    if (!inv) return { total: 0, perRoom: [], unavailable: 'No rate card for these dates' };
    const rate: HotelRateInput = {
      fixedRoomCost: inv.fixedRoomCost,
      adultPrice: inv.adultPrice,
      childWithBedPrice: inv.childWithBedPrice,
      childWithoutBedPrice: inv.childWithoutBedPrice,
      infantPrice: inv.infantPrice,
      extraAdultPrice: inv.extraAdultPrice,
      mealPerAdult: 0,
      mealPerChild: 0,
      baseAdults: inv.baseOccupancy ?? inv.occupancyPaxCount ?? 2,
      baseChildrenIncluded: 1,
      maxOccupancy: room.maxOccupancy,
    };
    let total = 0;
    const perRoom: number[] = [];
    for (const r of configRooms) {
      const p = priceRoomNight(rate, {
        adults: r.adults,
        children: r.children,
        childrenNoBed: r.childrenNoBed,
        infants: r.infants,
      });
      if (p.unavailableReason) return { total: 0, perRoom: [], unavailable: p.unavailableReason };
      total += p.amount;
      perRoom.push(p.amount * nights);
    }
    return { total: total * nights, perRoom, unavailable: null };
  };

  const pick = (room: HotelRoomOut): void => {
    const inv = invByRoom.get(room.id) ?? null;
    const { total, perRoom, unavailable } = priceRoomType(room, inv);
    if (unavailable) return; // can't quote an impossible room
    const stayTotalPaise = String(Math.round(total * 100));
    const mealPlanName = inv?.mealPlanName ?? room.mealPlanCode ?? null;

    void onPick({
      kind: 'HOTEL',
      source: 'DB',
      title: hotel.name,
      hotelId: hotel.id,
      roomTypeId: room.roomTypeId ?? null,
      occupancyTypeId: room.occupancyTypeId ?? null,
      mealPlanId: room.mealPlanId ?? null,
      costPaise: stayTotalPaise,
      markupPaise: '0',
      isRefundable: false,
      startDate: checkIn || null,
      endDate: checkOut || null,
      metaJson: {
        provider: 'PREFERRED',
        hotelName: hotel.name,
        starRating: hotel.starRating,
        cityName: hotel.cityName ?? cityName,
        roomName: room.name,
        // Category / occupancy labels for day-card + share Hotel Details.
        roomCategory: room.roomTypeName ?? null,
        occupancyTypeName: room.occupancyTypeName ?? null,
        mealPlanName,
        nights,
        rooms: roomsCount,
        checkIn,
        checkOut,
        // Source currency - ComponentsService.create FX-converts costPaise to
        // the itinerary currency (INR) on save, same as activities/transfers.
        currencyCode: inv?.currencyCode ?? currency,
        // Per-room stay price so the API splits this hotel across rooms by
        // cost. Only when the priced rooms ARE the itinerary's real rooms
        // (this picker never uses the per-stay override; the 1-room
        // fallback when no rooms are configured is not global).
        ...(roomsAreGlobal ? { roomCostWeights: perRoom } : {}),
      },
    });
  };

  // Render the hotel header through the SAME shared card the live/TBO picker
  // uses, so preferred + live results look identical. Fed a lightweight DB
  // HOTEL draft (no single price - preferred price varies by room, shown per
  // room below).
  const headerComponent = useMemo(
    () =>
      payloadToDraft(
        {
          kind: 'HOTEL',
          source: 'DB',
          title: hotel.name,
          hotelId: hotel.id,
          costPaise: '0',
          isRefundable: false,
          startDate: checkIn || null,
          endDate: checkOut || null,
          metaJson: {
            provider: 'PREFERRED',
            hotelName: hotel.name,
            starRating: hotel.starRating,
            cityName: hotel.cityName ?? cityName,
            hotelChain: hotel.hotelChain ?? null,
            nights,
            checkIn,
            checkOut,
          },
        },
        null,
        itineraryId,
      ),
    [hotel, checkIn, checkOut, cityName, nights, itineraryId],
  );

  return (
    <li className="list-none">
      <ComponentCard
        editable={false}
        onEdit={() => {}}
        onRemove={() => {}}
        fetchLiveDetails={false}
        component={headerComponent}
        actionSlot={
          <div className="flex flex-col items-end gap-1">
            <UzBadge tone="secondary">{hotel.roomCount} rooms</UzBadge>
            <UzButton size="sm" variant="outline" className="h-7 text-xs" onClick={onToggle}>
              {expanded ? 'Hide rooms' : 'Select room'}
              <ChevronDown
                className={'ml-1 h-3 w-3 transition-transform ' + (expanded ? 'rotate-180' : '')}
                aria-hidden
              />
            </UzButton>
          </div>
        }
      />

      {expanded && (
        <div className="mt-1 space-y-1.5 rounded-xl border border-zinc-200 bg-zinc-50/60 p-3">
          {roomsQuery.isLoading || invQuery.isLoading ? (
            <div className="flex items-center gap-2 py-2 text-xs text-zinc-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              Fetching rooms...
            </div>
          ) : visibleRooms.length === 0 ? (
            <p className="py-2 text-xs text-zinc-500">
              {rooms.length === 0
                ? 'No rooms configured for this hotel.'
                : 'None of the rooms match the meal filter. Clear it to see all options.'}
            </p>
          ) : (
            visibleRooms.map((room) => {
              const inv = invByRoom.get(room.id) ?? null;
              const { total, unavailable } = priceRoomType(room, inv);
              // Show INR (converted at list-time), same as activities/transfers.
              const totalInr = total * (inv?.fxRateToInr ?? 1);
              const mealName = inv?.mealPlanName ?? room.mealPlanCode ?? null;
              return (
                <div
                  key={room.id}
                  className="flex flex-col gap-2 rounded-lg border border-zinc-200 bg-white p-2.5"
                >
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium break-words text-zinc-900">{room.name}</p>
                      <div className="mt-0.5 flex flex-wrap gap-1.5">
                        {room.occupancyTypeName && (
                          <UzBadge tone="secondary">{room.occupancyTypeName}</UzBadge>
                        )}
                        {mealName && <MealBadge mealType={mealName} />}
                        <UzBadge tone="secondary">max {room.maxOccupancy} pax</UzBadge>
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end">
                      {unavailable ? (
                        <span className="text-[11px] text-zinc-400">{unavailable}</span>
                      ) : (
                        <>
                          <AdminOnlyAmount
                            costPaise={String(Math.round(totalInr * 100))}
                            currency="INR"
                            className="text-xs font-semibold text-zinc-900 tabular-nums"
                          />
                          <span className="text-[10px] text-zinc-500">
                            {nights}N × {roomsCount} room{roomsCount === 1 ? '' : 's'}
                          </span>
                        </>
                      )}
                    </div>
                    <UzButton
                      size="sm"
                      variant="outline"
                      className="h-7 shrink-0 text-xs"
                      loading={pending}
                      disabled={Boolean(unavailable)}
                      onClick={() => pick(room)}
                    >
                      Select
                    </UzButton>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </li>
  );
};
